import { useCallback, useEffect, useRef, useState } from 'react';

const readViewport = () => {
  if (typeof window === 'undefined') {
    return { viewportHeight: 0, keyboardOpen: false, keyboardHeight: 0, offsetTop: 0, supported: false };
  }
  const vv = window.visualViewport;
  if (!vv) {
    return {
      viewportHeight: window.innerHeight,
      keyboardOpen: false,
      keyboardHeight: 0,
      offsetTop: 0,
      supported: false,
    };
  }
  return {
    viewportHeight: vv.height,
    keyboardOpen: false,
    keyboardHeight: 0,
    offsetTop: vv.offsetTop || 0,
    supported: true,
  };
};

const sameViewport = (a, b) => (
  a.viewportHeight === b.viewportHeight
  && a.keyboardOpen === b.keyboardOpen
  && a.keyboardHeight === b.keyboardHeight
  && a.offsetTop === b.offsetTop
  && a.supported === b.supported
);

export default function useVisualViewport() {
  const [viewport, setViewport] = useState(readViewport);
  const stateRef = useRef(viewport);
  const baselineRef = useRef(viewport.supported ? viewport.viewportHeight : null);
  const pendingRef = useRef(null);
  const resetBaselineRef = useRef(false);

  const scheduleUpdate = useCallback((resetBaseline = false) => {
    if (resetBaseline) resetBaselineRef.current = true;
    if (pendingRef.current !== null) return;

    pendingRef.current = window.requestAnimationFrame(() => {
      pendingRef.current = null;
      const shouldResetBaseline = resetBaselineRef.current;
      resetBaselineRef.current = false;
      const vv = window.visualViewport;
      let next;

      if (!vv) {
        baselineRef.current = null;
        next = {
          viewportHeight: window.innerHeight,
          keyboardOpen: false,
          keyboardHeight: 0,
          offsetTop: 0,
          supported: false,
        };
      } else {
        const viewportHeight = vv.height;
        if (shouldResetBaseline || baselineRef.current === null) {
          baselineRef.current = viewportHeight;
        } else {
          baselineRef.current = Math.max(baselineRef.current, viewportHeight);
        }
        const baseline = baselineRef.current;
        const keyboardHeight = Math.max(0, baseline - viewportHeight);
        next = {
          viewportHeight,
          keyboardOpen: keyboardHeight > Math.max(100, baseline * 0.15),
          keyboardHeight,
          offsetTop: vv.offsetTop || 0,
          supported: true,
        };
      }

      if (!sameViewport(stateRef.current, next)) {
        stateRef.current = next;
        setViewport(next);
      }
    });
  }, []);

  useEffect(() => {
    const vv = window.visualViewport;
    const onViewportChange = () => scheduleUpdate(false);
    const onWindowChange = () => scheduleUpdate(true);

    window.addEventListener('resize', onWindowChange);
    window.addEventListener('orientationchange', onWindowChange);
    if (vv) {
      vv.addEventListener('resize', onViewportChange);
      vv.addEventListener('scroll', onViewportChange);
    }
    scheduleUpdate(false);

    return () => {
      window.removeEventListener('resize', onWindowChange);
      window.removeEventListener('orientationchange', onWindowChange);
      if (vv) {
        vv.removeEventListener('resize', onViewportChange);
        vv.removeEventListener('scroll', onViewportChange);
      }
      if (pendingRef.current !== null) {
        window.cancelAnimationFrame(pendingRef.current);
        pendingRef.current = null;
      }
    };
  }, [scheduleUpdate]);

  return viewport;
}
